import Cookies from 'js-cookie';
export const PURPOSE_LIMITATIONS_KEY = 'purposeLimitations';
export const CONTEXT_FRAMES_KEY = 'contextFrames';
export const NAMES_KEY = 'names';
/**
 * @name NOT_REQUIRED_INLINE_CONTEXT_LEVEL
 * @description The context with a specific level should be skipped for the user in inline component type.
 * @const {number}
 */
export const NOT_REQUIRED_INLINE_CONTEXT_LEVEL = 1;

/**
 * @name NOT_REQUIRED_MODAL_CONTEXT_LEVEL
 * @description The context with a specific level should be skipped for the user in modal component type.
 * @const {number}
 */
export const NOT_REQUIRED_MODAL_CONTEXT_LEVEL = 2;

/**
 * Uses in case if you want to test specific context frame version.
 * Possible values: '_v2'
 */
export const CONTEXT_FRAME_VERSION = Cookies.get('ni_cf_override') || '_v2';

/**
 * Name of ni_c cookies
 */
export const NI_C_COOKIES = `ni_c`;

/**
 * Prefix of location in ni_c cookies
 */
export const NI_C_LOCATION_PREFIX = `cc=`;

/**
 * Name of sq cookies
 */
export const SQ_COOKIES = `sq`;

/**
 * @name CONTEXT_FRAME_NAMES
 * @description Enum for representing context frame names value.
 * @enum {string}
 * @typedef CONTEXT_FRAME_NAMES
 * @property {string} WEB_GUEST_MODAL - Modal that guests see on first visit
 * @property {string} WEB_MEMBER_MODAL - Modal members see post-login
 * @property {string} WEB_MEMBER_SETTINGS - Privacy settings page for members
 * @property {string} WEB_GUEST_SETTINGS - Privacy settings page for guests
 */
export const CONTEXT_FRAME_NAMES = {
  WEB_GUEST_MODAL: `webGuestModal${CONTEXT_FRAME_VERSION}`,
  WEB_MEMBER_MODAL: `webMemberModal${CONTEXT_FRAME_VERSION}`,
  WEB_MEMBER_SETTINGS: `webMemberSettings${CONTEXT_FRAME_VERSION}`,
  WEB_GUEST_SETTINGS: `webGuestSettings${CONTEXT_FRAME_VERSION}`
};

/**
 * @name SERVER_CONTEXT_FRAME_NAMES
 * @description Enum for representing context frame names for not transformed values from server.
 * @enum {string}
 * @typedef SERVER_CONTEXT_FRAME_NAMES
 * @property {string} WEB_GUEST_MODAL - Modal that guests see on first visit
 * @property {string} WEB_MEMBER_MODAL - Modal members see post-login
 * @property {string} WEB_MEMBER_SETTINGS - Privacy settings page for members
 * @property {string} WEB_GUEST_SETTINGS - Privacy settings page for guests
 */
export const SERVER_CONTEXT_FRAME_NAMES = {
  WEB_GUEST_MODAL: `web_guest_modal${CONTEXT_FRAME_VERSION}`,
  WEB_MEMBER_MODAL: `web_member_modal${CONTEXT_FRAME_VERSION}`,
  WEB_MEMBER_SETTINGS: `web_member_settings${CONTEXT_FRAME_VERSION}`,
  WEB_GUEST_SETTINGS: `web_guest_settings${CONTEXT_FRAME_VERSION}`
};

/**
 * @name PURPOSE_LIMITATION_NAMES
 * @description Enum for targeting Purpose Limitation names.
 * @enum {string}
 */
export let PURPOSE_LIMITATION_NAMES = /*#__PURE__*/function (PURPOSE_LIMITATION_NAMES) {
  PURPOSE_LIMITATION_NAMES["PERSONALIZATION_RECOMMENDATION"] = "personalizationRecommendation";
  PURPOSE_LIMITATION_NAMES["TARGETING_BEHAVIORAL_EVENTS"] = "adtargetingBehavioralevents";
  PURPOSE_LIMITATION_NAMES["PERFORMANCE"] = "performance";
  PURPOSE_LIMITATION_NAMES["TARGETING_HASHEDMATCH"] = "adtargetingHashedmatch";
  PURPOSE_LIMITATION_NAMES["PERSONALIZATION_TARGETED_COMMS"] = "personalizationTargetedComms";
  return PURPOSE_LIMITATION_NAMES;
}({});

/**
 * @name SERVER_PURPOSE_LIMITATION_NAMES
 * @description Enum for targeting Purpose Limitation names for not transformed values from server.
 * @enum {string}
 */
export let SERVER_PURPOSE_LIMITATION_NAMES = /*#__PURE__*/function (SERVER_PURPOSE_LIMITATION_NAMES) {
  SERVER_PURPOSE_LIMITATION_NAMES["PERSONALIZATION_RECOMMENDATION"] = "personalization_recommendation";
  SERVER_PURPOSE_LIMITATION_NAMES["TARGETING_BEHAVIORAL_EVENTS"] = "adtargeting_behavioralevents";
  SERVER_PURPOSE_LIMITATION_NAMES["PERFORMANCE"] = "performance";
  SERVER_PURPOSE_LIMITATION_NAMES["TARGETING_HASHEDMATCH"] = "adtargeting_hashedmatch";
  SERVER_PURPOSE_LIMITATION_NAMES["PERSONALIZATION_TARGETED_COMMS"] = "personalization_targeted_comms";
  return SERVER_PURPOSE_LIMITATION_NAMES;
}({});

/**
 * @name COOKIE_PURPOSE_LIMITATION_NAMES
 * @description Enum for Purpose Limitation names' cookie values used within ni_c cookie key.
 * @enum {string}
 */
export let COOKIE_PURPOSE_LIMITATION_NAMES = /*#__PURE__*/function (COOKIE_PURPOSE_LIMITATION_NAMES) {
  COOKIE_PURPOSE_LIMITATION_NAMES["TARGETING_BEHAVIORAL_EVENTS"] = "BEAD";
  COOKIE_PURPOSE_LIMITATION_NAMES["PERFORMANCE"] = "PERF";
  COOKIE_PURPOSE_LIMITATION_NAMES["TARGETING_HASHEDMATCH"] = "1PA";
  COOKIE_PURPOSE_LIMITATION_NAMES["PERSONALIZATION_TARGETED_COMMS"] = "PERS";
  return COOKIE_PURPOSE_LIMITATION_NAMES;
}({});

/**
 * @name CONSENT_TYPE
 * @description Enum for representing purposeLimitation consent type.
 * @enum {string}
 */
export let CONSENT_TYPE = /*#__PURE__*/function (CONSENT_TYPE) {
  CONSENT_TYPE["ACCEPT"] = "accept";
  CONSENT_TYPE["DECLINE"] = "decline";
  CONSENT_TYPE["DEFAULT_ACCEPT"] = "default_accept";
  CONSENT_TYPE["DEFAULT_DECLINE"] = "default_decline";
  return CONSENT_TYPE;
}({});

/**
 * @name CONSENT_TYPE_MEMBER
 * @description Enum for representing purposeLimitation consent type for members.
 * @enum {string}
 */
export let CONSENT_TYPE_MEMBER = /*#__PURE__*/function (CONSENT_TYPE_MEMBER) {
  CONSENT_TYPE_MEMBER["ACCEPT"] = "accept";
  CONSENT_TYPE_MEMBER["DECLINE"] = "decline";
  CONSENT_TYPE_MEMBER["DEFAULT_ACCEPT"] = "default_accept";
  CONSENT_TYPE_MEMBER["DEFAULT_DECLINE"] = "default_decline";
  return CONSENT_TYPE_MEMBER;
}({});

/**
 * @name CONSENT_TYPE_GUEST
 * @description Enum for representing purposeLimitation consent type for guests.
 * @enum {string}
 */
export let CONSENT_TYPE_GUEST = /*#__PURE__*/function (CONSENT_TYPE_GUEST) {
  CONSENT_TYPE_GUEST["DEFAULT_ACCEPT"] = "default_accept";
  CONSENT_TYPE_GUEST["DEFAULT_DECLINE"] = "default_decline";
  return CONSENT_TYPE_GUEST;
}({});

/**
 * @name CONSENT_SELECTION_SOURCE
 * @description Enum for representing the source of consent selection.
 * @enum {string}
 */
export let CONSENT_SELECTION_SOURCE = /*#__PURE__*/function (CONSENT_SELECTION_SOURCE) {
  CONSENT_SELECTION_SOURCE["DIRECT"] = "DIRECT";
  CONSENT_SELECTION_SOURCE["ADVERTISING_DATA_SHARING_GPC_SWITCH"] = "ADVERTISING_DATA_SHARING_GPC_SWITCH";
  CONSENT_SELECTION_SOURCE["ADVERTISING_DATA_SHARING_CPRA_SWITCH"] = "ADVERTISING_DATA_SHARING_CPRA_SWITCH";
  return CONSENT_SELECTION_SOURCE;
}({});

/**
 * @name INLINE_COOKIE_EVENT_NAMESPACE
 * @description Const representing namespace for inline cookie custom events.
 * @const {string}
 */
export const INLINE_COOKIE_EVENT_NAMESPACE = '@nikeInlineCookieCustomEvent/';

/**
 * @name PRIVACY_CORE_EVENT_NAMESPACE
 * @description Const representing namespace for privacy core custom events.
 * @const {string}
 */
export const PRIVACY_CORE_EVENT_NAMESPACE = '@nikePrivacyCoreCustomEvent/';

/**
 * @name INLINE_COOKIE_CUSTOM_EVENT_NAMES
 * @description Object containing inline cookie custom event names.
 * @const {object}
 */
export let INLINE_COOKIE_CUSTOM_EVENT_NAMES = /*#__PURE__*/function (INLINE_COOKIE_CUSTOM_EVENT_NAMES) {
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["MFE_LOADED"] = "mfeLoaded";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["INITIALIZE_ERROR"] = "initializeError";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["PERMISSIONS_UPDATE_ERROR"] = "permissionsUpdateError";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["LEARN_MORE_IS_SHOWN"] = "learnMoreIsShown";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["LEARN_MORE_IS_HIDDEN"] = "learnMoreIsHidden";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["SHOW_LOADER"] = "showLoader";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["HIDE_LOADER"] = "hideLoader";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["SHOW_INLINE_ERROR"] = "showInlineError";
  INLINE_COOKIE_CUSTOM_EVENT_NAMES["SHOW_UPDATE_ERROR"] = "showUpdateError";
  return INLINE_COOKIE_CUSTOM_EVENT_NAMES;
}({});

/**
 * @name PRIVACY_CORE_CUSTOM_EVENT_NAMES
 * @description Object containing privacy core custom event names.
 * @const {object}
 */
export let PRIVACY_CORE_CUSTOM_EVENT_NAMES = /*#__PURE__*/function (PRIVACY_CORE_CUSTOM_EVENT_NAMES) {
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["INITIALIZED"] = "initialized";
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["INITIALIZE_ERROR"] = "initializeError";
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["CONTEXT_FRAMES_GET_ERROR"] = "contextFramesGetError";
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["PERMISSIONS_UPDATING"] = "permissionsUpdating";
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["PERMISSIONS_UPDATED"] = "permissionsUpdated";
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["PERMISSIONS_GET_ERROR"] = "permissionsGetError";
  PRIVACY_CORE_CUSTOM_EVENT_NAMES["PERMISSIONS_UPDATE_ERROR"] = "permissionsUpdateError";
  return PRIVACY_CORE_CUSTOM_EVENT_NAMES;
}({});

/**
 * @name RELIC_ACTION_TYPES_NAME
 * @description Object containing newrelic action types names. Using for get newrelic event name.
 * @const {object}
 */
export let RELIC_ACTION_TYPES_NAME = /*#__PURE__*/function (RELIC_ACTION_TYPES_NAME) {
  RELIC_ACTION_TYPES_NAME["SETTINGS_PAGE_VIEWED"] = "settingsPageViewed";
  RELIC_ACTION_TYPES_NAME["ACCEPT_ALL_CLICKED"] = "acceptAllClicked";
  RELIC_ACTION_TYPES_NAME["DECLINE_ALL_CLICKED"] = "declineAllClicked";
  RELIC_ACTION_TYPES_NAME["CONFIRM_CHOICE_CLICKED"] = "confirmChoiceClicked";
  RELIC_ACTION_TYPES_NAME["GPC_ENABLED"] = "gpcEnabled";
  return RELIC_ACTION_TYPES_NAME;
}({});

/**
 * @name RELIC_EVENT_NAME
 * @description Object containing newrelic event names.
 * @const {object}
 */
export let RELIC_EVENT_NAME = /*#__PURE__*/function (RELIC_EVENT_NAME) {
  RELIC_EVENT_NAME["PRIVACY_SETTINGS_VIEWED"] = "PrivacySettingsViewed";
  RELIC_EVENT_NAME["PRIVACY_SETTINGS_ACCEPT_ALL"] = "PrivacySettingsAcceptAll";
  RELIC_EVENT_NAME["PRIVACY_SETTINGS_DECLINE_ALL"] = "PrivacySettingsDeclineAll";
  RELIC_EVENT_NAME["PRIVACY_SETTINGS_CONFIRM_CHOICES"] = "PrivacySettingsConfirmChoices";
  RELIC_EVENT_NAME["GPC_ENABLED"] = "gpcEnabled";
  RELIC_EVENT_NAME["PRIVACY_CORE_API_ERROR"] = "privacyCoreAPIError";
  RELIC_EVENT_NAME["INITIALIZE_ERROR"] = "initializeError";
  RELIC_EVENT_NAME["PERMISSIONS_GET_ERROR"] = "permissionsGetError";
  RELIC_EVENT_NAME["CONTEXT_FRAMES_GET_ERROR"] = "contextFramesGetError";
  RELIC_EVENT_NAME["PERMISSIONS_UPDATE_ERROR"] = "permissionsUpdateError";
  return RELIC_EVENT_NAME;
}({});

/**
 * Cookie modal class name.
 */
export const COOKIE_MODAL_BASE_CLASS_NAME = 'cookie-modal-base';
export const TRANSLATIONS_URL_BASE = `https://www.nike.com/assets/i18n`;

/**
 * @name JORDAN_DROPS_HOSTNAME
 * @description Hostname of the Jordan Drops experience. Used to detect when consent UI should
 * render the Jordan Drops visual variant instead of the default nike.com styling.
 * @const {string}
 */
export const JORDAN_DROPS_HOSTNAME = '232323232323.com';

/**
 * @name JORDAN_DROPS_CLASS_NAME
 * @description Class name applied to the root of a component tree when rendering the Jordan
 * Drops visual variant. All Jordan Drops style overrides for that tree should be scoped under
 * this single class in one stylesheet, rather than threaded through components as a prop.
 * @const {string}
 */
export const JORDAN_DROPS_CLASS_NAME = 'jordan-drops';